import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    ServiceTemplateSchedulePathParamsSchema,
    ServiceTemplateScheduleIdPathParamsSchema,
    ServiceTemplateScheduleListQuerySchema,
    ServiceTemplateScheduleCreateSchema,
    ServiceTemplateScheduleUpdateSchema,
    ServiceTemplateScheduleBulkActionBodySchema,
    ServiceTemplateScheduleBulkActionResponseSchema,
} from "../schemas/service-template-schedule.schema";
import { ServiceTemplateScheduleSchema } from "../types/serviceTemplateSchedule";
import {
    OccurrenceListQuerySchema,
    OccurrenceResponseSchema,
    ResolvedOccurrenceResponseSchema,
    OccurrenceRoleOverrideSchema,
    OccurrenceBulkActionBodySchema,
    OccurrenceBulkActionResponseSchema,
    OccurrenceBulkRoleAssignmentBodySchema,
    CapacityPlanQuerySchema,
    CapacityPlanResponseSchema,
} from "../schemas/serviceTemplateRecurrenceOccurrence.schema";

export {
    ServiceTemplateScheduleCreateSchema,
    ServiceTemplateScheduleUpdateSchema,
};

export type {
    ServiceTemplateScheduleCreate,
    ServiceTemplateScheduleUpdate,
    ServiceTemplateScheduleListQuery,
    ServiceTemplateScheduleBulkActionType,
    ServiceTemplateScheduleBulkActionBody,
    ServiceTemplateScheduleBulkActionResponse,
} from "../schemas/service-template-schedule.schema";

export type {
    OccurrenceListQuery,
    OccurrenceResponse,
    ResolvedOccurrenceResponse,
    OccurrenceRoleOverride,
    OccurrenceBulkActionType,
    OccurrenceBulkActionBody,
    OccurrenceBulkActionResponse,
    OccurrenceBulkRoleAssignmentField,
    OccurrenceBulkRoleAssignmentBody,
    CapacityPlanQuery,
    CapacityPlanResponse,
} from "../schemas/serviceTemplateRecurrenceOccurrence.schema";

const c = initContract();

const OccurrenceIdParamsSchema = z.object({
    organizationId: z.string().uuid(),
    occurrenceId: z.string().uuid(),
});

const OrgIdOnlyParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const serviceTemplateScheduleContract = c.router(
    {
        listServiceTemplateSchedules: {
            method: "GET",
            path: "/organizations/:organizationId",
            pathParams: ServiceTemplateSchedulePathParamsSchema,
            query: ServiceTemplateScheduleListQuerySchema,
            responses: {
                200: z.object({
                    schedules: z.array(ServiceTemplateScheduleSchema),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getServiceTemplateSchedule: {
            method: "GET",
            path: "/organizations/:organizationId/:scheduleId",
            pathParams: ServiceTemplateSchedulePathParamsSchema.merge(
                ServiceTemplateScheduleIdPathParamsSchema,
            ),
            responses: {
                200: ServiceTemplateScheduleSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createServiceTemplateSchedule: {
            method: "POST",
            path: "/organizations/:organizationId",
            pathParams: ServiceTemplateSchedulePathParamsSchema,
            body: ServiceTemplateScheduleCreateSchema,
            responses: {
                201: ServiceTemplateScheduleSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        updateServiceTemplateSchedule: {
            method: "PATCH",
            path: "/organizations/:organizationId/:scheduleId",
            pathParams: ServiceTemplateSchedulePathParamsSchema.merge(
                ServiceTemplateScheduleIdPathParamsSchema,
            ),
            body: ServiceTemplateScheduleUpdateSchema,
            responses: {
                200: ServiceTemplateScheduleSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteServiceTemplateSchedule: {
            method: "DELETE",
            path: "/organizations/:organizationId/:scheduleId",
            pathParams: ServiceTemplateSchedulePathParamsSchema.merge(
                ServiceTemplateScheduleIdPathParamsSchema,
            ),
            responses: {
                200: c.noBody(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkScheduleAction: {
            method: "POST",
            path: "/organizations/:organizationId/bulk-action",
            pathParams: ServiceTemplateSchedulePathParamsSchema,
            body: ServiceTemplateScheduleBulkActionBodySchema,
            responses: {
                200: ServiceTemplateScheduleBulkActionResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listOccurrences: {
            method: "GET",
            path: "/organizations/:organizationId/:scheduleId/occurrences",
            pathParams: ServiceTemplateSchedulePathParamsSchema.merge(
                ServiceTemplateScheduleIdPathParamsSchema,
            ),
            query: OccurrenceListQuerySchema,
            responses: {
                200: z.object({
                    occurrences: z.array(ResolvedOccurrenceResponseSchema),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        skipOccurrence: {
            method: "POST",
            path: "/organizations/:organizationId/occurrences/:occurrenceId/skip",
            pathParams: OccurrenceIdParamsSchema,
            body: c.noBody(),
            responses: {
                200: OccurrenceResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        unskipOccurrence: {
            method: "POST",
            path: "/organizations/:organizationId/occurrences/:occurrenceId/unskip",
            pathParams: OccurrenceIdParamsSchema,
            body: c.noBody(),
            responses: {
                200: OccurrenceResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        applyOccurrenceNow: {
            method: "POST",
            path: "/organizations/:organizationId/occurrences/:occurrenceId/apply",
            pathParams: OccurrenceIdParamsSchema,
            body: c.noBody(),
            responses: {
                200: OccurrenceResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateOccurrenceRoleOverride: {
            method: "PATCH",
            path: "/organizations/:organizationId/occurrences/:occurrenceId/role-assignment",
            pathParams: OccurrenceIdParamsSchema,
            body: OccurrenceRoleOverrideSchema,
            responses: {
                200: OccurrenceResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkOccurrenceAction: {
            method: "POST",
            path: "/organizations/:organizationId/occurrences/bulk-action",
            pathParams: OrgIdOnlyParamsSchema,
            body: OccurrenceBulkActionBodySchema,
            responses: {
                200: OccurrenceBulkActionResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkUpdateOccurrenceRoleAssignment: {
            method: "PATCH",
            path: "/organizations/:organizationId/occurrences/bulk-role-assignment",
            pathParams: OrgIdOnlyParamsSchema,
            body: OccurrenceBulkRoleAssignmentBodySchema,
            responses: {
                200: OccurrenceBulkActionResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getCapacityPlan: {
            method: "GET",
            path: "/organizations/:organizationId/occurrences/capacity-plan",
            pathParams: OrgIdOnlyParamsSchema,
            query: CapacityPlanQuerySchema,
            responses: {
                200: CapacityPlanResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/service-template-schedules",
    },
);
